import { fetchStackFramesPreviewForRunKey } from '@cotera/client/app/hooks/stack-frame-fetch';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { ShimmerText } from '@cotera/client/v0/components/ui/shimmer-text';
import { Relation } from '@cotera/era';
import { DG } from '@cotera/sdk/core';
import React, { Suspense, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ModelScope,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { DefaultDetailViewCell } from '../detail-view';
import { AtcCellModel, mapPreviewToFull } from './cell.model';
import { AtcContext, useAtcCellModel } from './context';
import { AtcCellInViewGate } from './atc-cell-in-view-gate';
import { DynamicStatusText, hasWarehouseValue } from './shared';
import {
  getEditorStateFromGraph,
  getLlmConfigFromGraph,
} from '../../columns/llm/utils/get-editor-state-from-graph';

// ATC Detail View Components
type DetailViewCellProps = {
  column: string;
  value: any;
  rel: Relation;
  focused: boolean;
};

const AtcSuspensefulDetailViewCellComponentForRunKey = React.forwardRef<
  HTMLDivElement,
  DetailViewCellProps & {
    runKey: string;
    startingIdentity: DG.Graph.Identity | null;
    currentIdentity: DG.Graph.Identity | null;
    detailViewCellRenderer: React.ForwardRefExoticComponent<
      DetailViewCellProps & React.RefAttributes<HTMLDivElement>
    >;
  }
>((props, ref) => {
  const cellModel = useAtcCellModel();
  const data = useWatchableValue(cellModel.stackFramesWatchable);

  const renderContent = (): React.ReactNode => {
    // Initial loading
    if (data === null) {
      return <props.detailViewCellRenderer {...props} ref={ref} />;
    }

    if (data.isErr()) {
      if (hasWarehouseValue(props.value)) {
        return <props.detailViewCellRenderer {...props} ref={ref} />;
      }
      return (
        <DefaultDetailViewCell
          ref={ref}
          title={props.column}
          focused={props.focused}
        >
          <Badge variant={'destructive'}>Error</Badge>
        </DefaultDetailViewCell>
      );
    }

    if (data.value === null) {
      const isLatestIdentity =
        props.currentIdentity === null ||
        props.startingIdentity?.eq(props.currentIdentity);
      // If not latest identity, don't show existing values as they will be stale
      if (hasWarehouseValue(props.value) && isLatestIdentity) {
        return <props.detailViewCellRenderer {...props} ref={ref} />;
      }
      return (
        <DefaultDetailViewCell
          ref={ref}
          title={props.column}
          focused={props.focused}
        >
          {props.currentIdentity === null ? (
            <Badge variant="muted">Not Run</Badge>
          ) : props.startingIdentity?.eq(props.currentIdentity) ? (
            <Badge variant="muted">Not Run</Badge>
          ) : (
            <ShimmerText variant="muted">Waiting</ShimmerText>
          )}
        </DefaultDetailViewCell>
      );
    }

    // Get the root frame from the response
    const rootFrame = data.value.frames[0];
    if (!rootFrame) {
      return <props.detailViewCellRenderer {...props} ref={ref} />;
    }

    if (rootFrame.status === 'failed' || rootFrame.status === 'stalled') {
      return (
        <DefaultDetailViewCell
          ref={ref}
          title={props.column}
          focused={props.focused}
        >
          <Badge variant={'destructive'}>Failed</Badge>
        </DefaultDetailViewCell>
      );
    }

    if (rootFrame.status === 'cancelled') {
      return (
        <DefaultDetailViewCell
          ref={ref}
          title={props.column}
          focused={props.focused}
        >
          <Badge variant={'gray'}>Cancelled</Badge>
        </DefaultDetailViewCell>
      );
    }

    if (rootFrame.status === 'completed') {
      // Safe to access .val as atc outputs are guaranteed to have shape {ty: string; val: any}
      const { output } = rootFrame;
      const value = output === null ? null : output['val'];
      return (
        <props.detailViewCellRenderer {...props} value={value} ref={ref} />
      );
    }

    // In-progress states - use dynamic status text with shimmer
    if (
      rootFrame.status === 'pending' ||
      rootFrame.status === 'running' ||
      rootFrame.status === 'waiting' ||
      rootFrame.status === 'allocated' ||
      rootFrame.status === 'queued'
    ) {
      return (
        <DefaultDetailViewCell
          ref={ref}
          title={props.column}
          focused={props.focused}
        >
          <DynamicStatusText frames={data.value.frames} />
        </DefaultDetailViewCell>
      );
    }

    return <props.detailViewCellRenderer {...props} ref={ref} />;
  };

  return (
    <AtcCellInViewGate cellModel={cellModel} pollingEnabled={true}>
      {renderContent()}
    </AtcCellInViewGate>
  );
});

const MemoedAtcSuspensefulDetailViewCellComponentForRunKey = React.memo(
  AtcSuspensefulDetailViewCellComponentForRunKey
);

const AtcSuspensefulDetailViewCellComponent = React.forwardRef<
  HTMLDivElement,
  DetailViewCellProps & {
    detailViewCellRenderer: React.ForwardRefExoticComponent<
      DetailViewCellProps & React.RefAttributes<HTMLDivElement>
    >;
  }
>((props, ref) => {
  const cellModel = useAtcCellModel();
  const runKey = useWatchableValue(cellModel.runKey);
  const colIdentity = useWatchableValue(cellModel.colIdentity);
  const startingIdentity = useWatchableValue(cellModel.startingIdentity);

  if (runKey === null) {
    return <props.detailViewCellRenderer {...props} ref={ref} />;
  }

  return (
    <MemoedAtcSuspensefulDetailViewCellComponentForRunKey
      {...props}
      runKey={runKey}
      startingIdentity={startingIdentity}
      currentIdentity={colIdentity}
      ref={ref}
    />
  );
});

export const AtcDetailViewCellComponent = React.forwardRef<
  HTMLDivElement,
  DetailViewCellProps & {
    relName: string;
    detailViewCellRenderer: React.ForwardRefExoticComponent<
      DetailViewCellProps & React.RefAttributes<HTMLDivElement>
    >;
    rowData: Record<string, any>;
    rowIndex: number;
  }
>((props, ref) => {
  const graphModel = useInScopeModel(GraphModel);
  const graph = useWatchableValue(graphModel.graph);
  const client = useTenantedClient();

  const fetchStackFrames = useCallback(
    async (runKey: string) => {
      const result = await fetchStackFramesPreviewForRunKey(client.atc, runKey);
      return result.map(mapPreviewToFull);
    },
    [client]
  );

  // Check if this column is an LLM or workflow column
  const columnConfig = useMemo(() => {
    const column = graph.getColumn({
      rel: props.relName,
      name: props.column,
    });
    return column?.config ?? null;
  }, [graph, props.relName, props.column]);

  const isAtcColumn =
    columnConfig?.t === 'llm' || columnConfig?.t === 'workflow';

  // Get LLM config for the column
  const llmConfig = useMemo(() => {
    if (columnConfig?.t !== 'llm') {
      return null;
    }
    return getLlmConfigFromGraph(graph, props.relName, props.column);
  }, [graph, props.relName, props.column, columnConfig]);

  // Get editor state for LLM columns
  const getEditorState = useMemo(() => {
    return () => {
      const editorState = getEditorStateFromGraph(
        graph,
        props.relName,
        props.column
      );
      if (!editorState) {
        throw new Error(`Editor state not found for column ${props.column}`);
      }
      return editorState;
    };
  }, [graph, props.relName, props.column]);

  // Create the cell model for ATC columns
  const cellModel = useMemo(
    () => {
      if (!isAtcColumn) {
        return null;
      }

      // For workflow columns, use default config
      const config = llmConfig ?? {
        model: '',
        schedule: 'never' as const,
        temperature: 0.5,
        maxOutputTokens: 4096,
      };

      return new AtcCellModel(
        props.rowIndex,
        props.column,
        props.relName,
        props.rowData?.['__cotera_stable_id'],
        {
          model: config.model,
          when: config.schedule,
          temperature: config.temperature,
          maxOutputTokens: config.maxOutputTokens,
          topP: 1,
          topK: 1,
          frequencyPenalty: 0,
          presencePenalty: 0,
        },
        getEditorState,
        graphModel.graph,
        false,
        fetchStackFrames,
        props.value
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAtcColumn,
      props.rowIndex,
      props.column,
      props.relName,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      props.rowData?.['__cotera_stable_id'],
      llmConfig,
      getEditorState,
      graphModel.graph,
      fetchStackFrames,
    ]
  );

  if (isAtcColumn && cellModel) {
    return (
      <ModelScope models={[cellModel]}>
        <AtcContext.Provider value={{ model: cellModel }}>
          <ErrorBoundary
            fallbackRender={() => (
              <DefaultDetailViewCell
                ref={ref}
                title={props.column}
                focused={props.focused}
              >
                <Badge variant={'muted'}>Client Error</Badge>
              </DefaultDetailViewCell>
            )}
          >
            <Suspense
              fallback={
                <DefaultDetailViewCell
                  ref={ref}
                  title={props.column}
                  focused={props.focused}
                >
                  <Badge variant={'muted'} className="animate-pulse">
                    Loading
                  </Badge>
                </DefaultDetailViewCell>
              }
            >
              <AtcSuspensefulDetailViewCellComponent {...props} ref={ref} />
            </Suspense>
          </ErrorBoundary>
        </AtcContext.Provider>
      </ModelScope>
    );
  }

  return <props.detailViewCellRenderer {...props} ref={ref} />;
});
